import React, { useRef } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { createFragmentContainer, graphql } from 'react-relay';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { trackNullSearchRelatedItemProvider } from './sbSharedNullSearchRelatedItemsTrackingHelpers';

// components
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { SbSharedSearchSuggestions } from '../SbSharedSearchSuggestions/SbSharedSearchSuggestions';
import { SbSharedNullSearchRelatedItemsFollowLazy } from './SbSharedNullSearchRelatedItemsFollowLazy';

// styles
import styles from './SbSharedNullSearchRelatedItems.scss';

export const SbSharedNullSearchRelatedItemsComponent = ({
    user,
    relay,
    viewer,
    isMobile,
    isClient,
    itemSearch,
    searchTerm,
    verticalOffset,
    shouldFetchFolder,
    lazyLoadStartIndex,
}) => {
    const attemptedToTrackItemIndexes = useRef([]);
    const { isFavoritesEnabledByLocale, regionalInfo } = viewer;
    const userId = user ? user.serviceId : null;
    const { relatedSearchResults } = itemSearch;
    const { edges: nodes = [] } = relatedSearchResults;
    const hasRelatedSearchItems = !!nodes.length;
    const userRegions = get(viewer, 'regionalInfo[0].regionsByZipCode') || [];

    const [measurementUnit] = useMeasurementUnit({
        user,
        itemSearch,
    });

    const { current: impressionTracking } = React.useRef(
        hasRelatedSearchItems
            ? trackNullSearchRelatedItemProvider({ nodes, searchTerm, userRegions })
            : {}
    );
    const {
        trackNullSearchRelatedItemsLoad = () => {},
        trackNullSearchRelatedItemClick = () => {},
        trackNullSearchRelatedItemsImpressions = () => {},
    } = impressionTracking;

    React.useEffect(() => {
        trackNullSearchRelatedItemsLoad();
    }, [trackNullSearchRelatedItemsLoad]);

    const onContentVisible = index => {
        if (!regionalInfo) {
            attemptedToTrackItemIndexes.current.push(index);
        } else {
            attemptedToTrackItemIndexes.current.forEach(attemptedToTrackItemIndex => {
                trackNullSearchRelatedItemsImpressions({ index: attemptedToTrackItemIndex });
            });
            attemptedToTrackItemIndexes.current = [];
            trackNullSearchRelatedItemsImpressions({ index });
        }
    };

    return hasRelatedSearchItems ? (
        <React.Fragment>
            <div className={styles.followHeader}>
                {isClient && (
                    <SbSharedNullSearchRelatedItemsFollowLazy
                        user={user}
                        itemSearch={itemSearch}
                        searchTerm={searchTerm}
                    />
                )}
            </div>
            <SbSharedSearchSuggestions
                searchTerm={searchTerm}
                itemSearch={itemSearch}
                className={styles.searchSuggestions}
            />
            <div className={styles.container}>
                <div className={styles.tilesHeader}>
                    <FormattedMessage
                        id="SbSharedNullSearchRelatedItems.tilesTitle"
                        defaultMessage="Showing results matching fewer words"
                    />
                </div>

                <FavoritesProvider
                    userId={userId}
                    disable={!isClient}
                    environment={relay.environment}
                    fetchFolder={shouldFetchFolder}
                    location={locations.SEARCH_BROWSE}
                    fetchHeart={isFavoritesEnabledByLocale}
                    itemIds={nodes.map(({ node }) => get(node, 'item.serviceId'))}
                >
                    {({ props: favorites }) => (
                        <QuickViewProvider>
                            {({ quickView }) => (
                                <div className={styles.tilesWrapper}>
                                    {nodes.map(({ node: { item } }, index) => {
                                        return (
                                            <SharedItemTile
                                                item={item}
                                                index={index}
                                                isClient={isClient}
                                                isMobile={isMobile}
                                                key={item.serviceId}
                                                favorites={favorites}
                                                showSellerName={false}
                                                quickViewProps={quickView}
                                                measurementUnit={measurementUnit}
                                                customTileWidthClass={styles.tile}
                                                lazyLoadStartIndex={lazyLoadStartIndex}
                                                imageLoadVerticalOffset={verticalOffset}
                                                onContentVisible={() => onContentVisible(index)}
                                                onClick={() =>
                                                    trackNullSearchRelatedItemClick({ index, item })
                                                }
                                                viewer={viewer}
                                                itemSearch={itemSearch}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </div>
        </React.Fragment>
    ) : null;
};

SbSharedNullSearchRelatedItemsComponent.propTypes = {
    user: PropTypes.object,
    isClient: PropTypes.bool,
    isMobile: PropTypes.bool,
    shouldFetchFolder: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    lazyLoadStartIndex: PropTypes.number,
    searchTerm: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    verticalOffset: PropTypes.number.isRequired,
};

export const SbSharedNullSearchRelatedItems = createFragmentContainer(
    SbSharedNullSearchRelatedItemsComponent,
    {
        itemSearch: graphql`
            fragment SbSharedNullSearchRelatedItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                relatedSearchResults {
                    edges {
                        node {
                            item {
                                title
                                serviceId
                                browseUrl
                                categoryCode
                                seller {
                                    serviceId
                                }
                                contemporaryTrackingString
                                ...SharedItemTile_item
                                ...freeShippingHelpers_item
                            }
                        }
                    }
                }
                ...useMeasurementUnit_itemSearch
                ...SbSharedSearchSuggestions_itemSearch
                ...SbSharedNullSearchRelatedItemsFollow_itemSearch
            }
        `,
        user: graphql`
            fragment SbSharedNullSearchRelatedItems_user on User {
                ...useMeasurementUnit_user
                ...SbSharedNullSearchRelatedItemsFollow_user
            }
        `,
        viewer: graphql`
            fragment SbSharedNullSearchRelatedItems_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
                isFavoritesEnabledByLocale: isEnabledByLocale(featureName: "FAVORITES")
                ...SharedItemTile_viewer
            }
        `,
    }
);
